// LegacyGaugeSlider — 旧 Java applet 風ゲージ式スライダの汎用 component.
//
// 元は `PenWidthSlider.tsx` の core ロジック (= `PaintToolCanvas.java:498-509`
// paint + `:792-816` mouseDragged の TS port). 筆圧設定 UI (`PenPressureConfig`)
// でも HTML 標準 `<input type="range">` から旧版風ゲージへ統一するため,
// 共通化した (consult 2026-05-09).
//
// 旧版踏襲:
//   - 横長の細いバー上に「現在値 / max」の比率で黒塗りゲージを描画.
//   - thumb は持たず, 黒塗りの右端がそのまま現在値.
//   - 外枠は `outsetFrame` (Win 95 風 1px 枠).
//
// Phase 6.7 R11 / R10 修正パターン (旧 `PenWidthSlider` から踏襲):
//   1. pointerdown でドラッグ開始フラグを useRef に保持
//   2. setPointerCapture で要素外でも pointermove を受け続ける
//   3. pointermove は dragging ref を参照 (再評価しない)
//   4. pointerup / pointercancel / lostpointercapture で release
//   5. 値変換 (`xToValue`) は内部 clamp に任せる (画面外までドラッグしても
//      valueMin / valueMax まで届く) — 早期 return しない.
//
// disabled=true のとき:
//   - 黒塗りゲージを灰色 (`bg-black/30`) に
//   - 全体 cursor を not-allowed
//   - PointerDown を early return (= ドラッグ無効)
//   - tabIndex は 0 維持 (= キーボード focus は維持、HTML disabled UX 標準)
//   - aria-disabled="true" を出す
//
// (architecture.md §16, classic-ui-restore.md §4 R11)

import { useRef } from 'react';

import { outsetFrame } from './legacyFrames.js';
import { valueToX, xToValue } from './legacyGaugeSlider.js';

export interface LegacyGaugeSliderProps {
  /** 現在値. valueMin..valueMax の範囲. */
  readonly value: number;
  /** 最小値 (例: 0). */
  readonly valueMin: number;
  /** 最大値 (例: 60 や 1). */
  readonly valueMax: number;
  /** 値の刻み. default 1 (整数 snap). 0.05 等で小数 snap も可. */
  readonly step?: number;
  /** disabled=true で操作無効化 + グレー表示. default false. */
  readonly disabled?: boolean;
  /** 値変更時に発火. 値は内部で valueMin..valueMax + step snap 済. */
  readonly onChange: (next: number) => void;
  /** スクリーンリーダ向け aria-label (必須). */
  readonly ariaLabel: string;
  /**
   * 値 → 読み上げ文字列の変換 (optional). 渡されたら aria-valuetext に展開し
   * SR-only span でも使う. 例: `(v) => `${Math.round(v * 100)}%`` で 0.5 → "50%".
   * 未指定なら value をそのまま toString.
   */
  readonly ariaValueText?: (v: number) => string;
  /** バー描画幅 (px). default 50. */
  readonly width?: number;
  /** バー描画高さ (px). default 8. */
  readonly height?: number;
  /** 使い方マニュアル (user-manual-2026-09-19.md §10.2) の `data-manual` id。 */
  readonly manualId?: string;
}

const DEFAULT_WIDTH = 50;
const DEFAULT_HEIGHT = 8;

export const LegacyGaugeSlider = ({
  value,
  valueMin,
  valueMax,
  step = 1,
  disabled = false,
  onChange,
  ariaLabel,
  ariaValueText,
  width = DEFAULT_WIDTH,
  height = DEFAULT_HEIGHT,
  manualId,
}: LegacyGaugeSliderProps): React.ReactNode => {
  /**
   * R11: ドラッグ中フラグ. pointerdown で true, pointermove は true 時のみ反応,
   * pointerup / lostpointercapture で false に戻す. 旧版 mouseDragged と同じ思想.
   */
  const dragging = useRef<boolean>(false);
  const barRef = useRef<HTMLDivElement | null>(null);

  /** Canvas 内 x 座標を計算して onChange を発火. */
  const applyFromClientX = (clientX: number): void => {
    const bar = barRef.current;
    if (!bar) return;
    const rect = bar.getBoundingClientRect();
    if (rect.width <= 0) return;
    // CSS px と内部 width が異なっても rect.width で比例計算 (= CSS で拡大されて
    // も正しく動く).
    const xCanvas = ((clientX - rect.left) / rect.width) * width;
    const next = xToValue(xCanvas, width, valueMin, valueMax, step);
    onChange(next);
  };

  const handleDown = (e: React.PointerEvent<HTMLDivElement>): void => {
    if (disabled) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    dragging.current = true;
    applyFromClientX(e.clientX);
  };

  const handleMove = (e: React.PointerEvent<HTMLDivElement>): void => {
    if (!dragging.current) return;
    if (e.buttons === 0) {
      // safety: ボタン離れ後の event を無視 (pointerup を取りこぼした場合).
      dragging.current = false;
      return;
    }
    applyFromClientX(e.clientX);
  };

  const handleUp = (): void => {
    dragging.current = false;
  };

  // ゲージ右端 (黒塗り部分の右端).
  const thumbX = valueToX(value, width, valueMin, valueMax);

  // 読み上げ文字列 (aria-valuetext + SR-only span 用).
  const valueText = ariaValueText ? ariaValueText(value) : String(value);

  return (
    <div
      ref={barRef}
      role="slider"
      aria-label={ariaLabel}
      aria-valuemin={valueMin}
      aria-valuemax={valueMax}
      aria-valuenow={value}
      aria-valuetext={valueText}
      aria-disabled={disabled || undefined}
      data-manual={manualId}
      tabIndex={0}
      onPointerDown={handleDown}
      onPointerMove={handleMove}
      onPointerUp={handleUp}
      onPointerCancel={handleUp}
      onLostPointerCapture={handleUp}
      // outsetFrame で旧版風枠. 内側に黒塗りゲージを 0..thumbX で表示.
      className={`relative bg-white ${outsetFrame}`}
      style={{
        width: `${width}px`,
        height: `${height}px`,
        cursor: disabled ? 'not-allowed' : 'ew-resize',
        touchAction: 'none',
      }}
    >
      {/* 黒塗りゲージ. disabled 時はグレー. */}
      <div
        aria-hidden
        className={`absolute top-0 left-0 h-full ${disabled ? 'bg-black/30' : 'bg-black'}`}
        style={{ width: `${thumbX}px` }}
      />
      {/* SR-only 表示で現在値を読み上げ可能にする (slider role 補完). */}
      <span className="sr-only">{valueText}</span>
    </div>
  );
};
